import React, { useState, useEffect, useCallback } from 'react';
import {
  Dialog, DialogTitle, DialogContent, DialogActions, Button, Box, Typography,
  TextField, MenuItem, Grid, Stack, Alert, CircularProgress, Chip, Divider,
  IconButton, List, ListItem, ListItemIcon, ListItemText, Checkbox, Tooltip,
  FormControlLabel, Tabs, Tab,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import DeleteIcon from '@mui/icons-material/Delete';
import SendIcon from '@mui/icons-material/Send';
import { tasksAPI } from '../../services/api';
import { DISCIPLINES, STATUS_LABELS, STATUS_COLORS } from '../../pages/technician/taskConstants';
import TaskPhoto from '../../pages/technician/TaskPhoto';

const PRIORITIES = ['baja', 'normal', 'alta', 'urgente'];

const EMPTY = {
  title: '', description: '', discipline: '', priority: 'normal',
  planned_start: '', planned_end: '', estimated_hours: '', location: '',
  safety_notes: '', required_tools: '', required_materials: '', notes: '',
  requires_photo: false, assigned_technician_id: '',
};

/**
 * Alta y edicion de una tarea de campo desde el gestor.
 *
 * Los mismos campos que el alta del movil, mas checklist y comentarios: es lo
 * que el tecnico se encuentra al abrir la tarea, asi que aqui es donde se le
 * dan las indicaciones (descripcion, seguridad, herramientas y materiales).
 *
 * En una tarea nueva el checklist se compone en local y se manda de una vez al
 * crearla; en una existente cada punto va contra la API, que devuelve la tarea
 * entera ya actualizada.
 */
export default function TaskEditorDialog({
  open, taskId = null, manufacturingOrderId = null, onClose, onSaved,
}) {
  const [form, setForm] = useState(EMPTY);
  const [task, setTask] = useState(null);        // solo en edicion
  const [technicians, setTechnicians] = useState([]);
  const [tab, setTab] = useState(0);
  const [loading, setLoading] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');

  const [newItem, setNewItem] = useState('');
  const [draftChecklist, setDraftChecklist] = useState([]);   // solo en alta
  const [comment, setComment] = useState('');

  const isNew = !taskId;

  const set = (key) => (e) => setForm((f) => ({ ...f, [key]: e.target.value }));

  const load = useCallback(async () => {
    if (!taskId) return;
    setLoading(true);
    try {
      const res = await tasksAPI.get(taskId);
      setTask(res.data);
      setForm({
        ...EMPTY,
        ...Object.fromEntries(
          Object.keys(EMPTY).map((k) => [k, res.data[k] ?? EMPTY[k]]),
        ),
        requires_photo: !!res.data.requires_photo,
        assigned_technician_id: res.data.assigned_technician_id || '',
      });
      setError('');
    } catch (e) {
      setError(e.response?.data?.detail || 'No se ha podido cargar la tarea.');
    } finally {
      setLoading(false);
    }
  }, [taskId]);

  useEffect(() => {
    if (!open) return;
    setTab(0);
    setNewItem('');
    setComment('');
    setDraftChecklist([]);
    setError('');
    if (taskId) {
      load();
    } else {
      setTask(null);
      setForm(EMPTY);
    }
  }, [open, taskId, load]);

  useEffect(() => {
    if (!open) return;
    const orderId = manufacturingOrderId || task?.manufacturing_order_id;
    tasksAPI.assignableTechnicians(orderId || undefined)
      .then((r) => {
        const team = r.data.team || [];
        const rest = (r.data.all || []).filter((t) => !t.in_team);
        setTechnicians([...team, ...rest]);
      })
      .catch(() => setTechnicians([]));
  }, [open, manufacturingOrderId, task?.manufacturing_order_id]);

  const payload = () => ({
    title: form.title.trim(),
    description: form.description || null,
    discipline: form.discipline || null,
    priority: form.priority || 'normal',
    planned_start: form.planned_start || null,
    planned_end: form.planned_end || null,
    estimated_hours: form.estimated_hours === '' ? null : parseFloat(form.estimated_hours),
    location: form.location || null,
    safety_notes: form.safety_notes || null,
    required_tools: form.required_tools || null,
    required_materials: form.required_materials || null,
    notes: form.notes || null,
    requires_photo: !!form.requires_photo,
  });

  const save = async () => {
    if (!form.title.trim()) {
      setError('El titulo es obligatorio.');
      return;
    }
    setBusy(true);
    setError('');
    try {
      if (isNew) {
        await tasksAPI.create({
          ...payload(),
          manufacturing_order_id: manufacturingOrderId || null,
          assigned_technician_id: form.assigned_technician_id || null,
          checklist: draftChecklist,
        });
      } else {
        await tasksAPI.update(taskId, payload());
        // La asignacion va aparte: deja constancia en el historico y mueve el
        // estado (pendiente <-> asignada) como corresponde
        if ((form.assigned_technician_id || null) !== (task?.assigned_technician_id || null)) {
          await tasksAPI.assign(taskId, {
            technician_id: form.assigned_technician_id || null,
          });
        }
      }
      if (onSaved) onSaved();
      onClose();
    } catch (e) {
      setError(e.response?.data?.detail || 'No se ha podido guardar la tarea.');
    } finally {
      setBusy(false);
    }
  };

  const addItem = async () => {
    const text = newItem.trim();
    if (!text) return;
    if (isNew) {
      setDraftChecklist((l) => [...l, text]);
      setNewItem('');
      return;
    }
    try {
      const res = await tasksAPI.addChecklistItem(taskId, text);
      setTask(res.data);
      setNewItem('');
    } catch (e) {
      setError(e.response?.data?.detail || 'No se ha podido anadir el punto.');
    }
  };

  const removeItem = async (item, idx) => {
    if (isNew) {
      setDraftChecklist((l) => l.filter((_, i) => i !== idx));
      return;
    }
    try {
      await tasksAPI.deleteChecklistItem(item.id);
      await load();
    } catch (e) {
      setError(e.response?.data?.detail || 'No se ha podido eliminar el punto.');
    }
  };

  const toggleItem = async (item) => {
    try {
      const res = await tasksAPI.toggleChecklistItem(item.id);
      setTask(res.data);
    } catch (e) {
      setError(e.response?.data?.detail || 'No se ha podido actualizar el punto.');
    }
  };

  const addComment = async () => {
    if (!comment.trim()) return;
    setBusy(true);
    try {
      const res = await tasksAPI.addUpdate(taskId, { comment: comment.trim() });
      setTask(res.data);
      setComment('');
      if (onSaved) onSaved();
    } catch (e) {
      setError(e.response?.data?.detail || 'No se ha podido guardar el comentario.');
    } finally {
      setBusy(false);
    }
  };

  const checklist = isNew
    ? draftChecklist.map((text, i) => ({ id: `draft-${i}`, text, is_done: false }))
    : (task?.checklist || []);

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle sx={{ pb: 0 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, flexWrap: 'wrap' }}>
          {isNew ? 'Nueva tarea' : task?.title || 'Tarea'}
          {task && <Chip size="small" label={task.code} variant="outlined" />}
          {task && (
            <Chip size="small" label={STATUS_LABELS[task.status] || task.status}
              color={STATUS_COLORS[task.status] || 'default'} />
          )}
        </Box>
        <Tabs value={tab} onChange={(_, v) => setTab(v)} sx={{ mt: 1 }}>
          <Tab label="Datos" />
          <Tab label={`Checklist (${checklist.length})`} />
          <Tab label={isNew ? 'Comentarios' : `Comentarios (${task?.updates?.length || 0})`}
            disabled={isNew} />
        </Tabs>
      </DialogTitle>

      <DialogContent dividers sx={{ minHeight: 420 }}>
        {error && <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>{error}</Alert>}

        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}><CircularProgress /></Box>
        ) : (
          <>
            {tab === 0 && (
              <Grid container spacing={2} sx={{ mt: 0 }}>
                <Grid item xs={12}>
                  <TextField fullWidth required autoFocus label="Que hay que hacer"
                    value={form.title} onChange={set('title')} />
                </Grid>

                <Grid item xs={12} sm={6}>
                  <TextField select fullWidth label="Asignar a"
                    value={form.assigned_technician_id} onChange={set('assigned_technician_id')}>
                    <MenuItem value=""><em>Sin asignar</em></MenuItem>
                    {technicians.map((t) => (
                      <MenuItem key={t.id} value={t.id}>
                        {t.name}{t.in_team ? ' · en la OF' : ''}
                      </MenuItem>
                    ))}
                  </TextField>
                </Grid>
                <Grid item xs={6} sm={3}>
                  <TextField fullWidth type="date" label="Inicio" InputLabelProps={{ shrink: true }}
                    value={form.planned_start || ''} onChange={set('planned_start')} />
                </Grid>
                <Grid item xs={6} sm={3}>
                  <TextField fullWidth type="date" label="Fin" InputLabelProps={{ shrink: true }}
                    value={form.planned_end || ''} onChange={set('planned_end')} />
                </Grid>

                <Grid item xs={6} sm={3}>
                  <TextField fullWidth label="Horas previstas" value={form.estimated_hours ?? ''}
                    onChange={(e) => setForm((f) => ({
                      ...f, estimated_hours: e.target.value.replace(',', '.'),
                    }))}
                    inputProps={{ inputMode: 'decimal' }} />
                </Grid>
                <Grid item xs={6} sm={3}>
                  <TextField select fullWidth label="Prioridad" value={form.priority || 'normal'}
                    onChange={set('priority')}>
                    {PRIORITIES.map((p) => <MenuItem key={p} value={p}>{p}</MenuItem>)}
                  </TextField>
                </Grid>
                <Grid item xs={6} sm={3}>
                  <TextField select fullWidth label="Disciplina" value={form.discipline || ''}
                    onChange={set('discipline')}>
                    <MenuItem value=""><em>Sin definir</em></MenuItem>
                    {DISCIPLINES.map((d) => <MenuItem key={d} value={d}>{d}</MenuItem>)}
                  </TextField>
                </Grid>
                <Grid item xs={6} sm={3}>
                  <TextField fullWidth label="Ubicacion" value={form.location || ''}
                    onChange={set('location')} />
                </Grid>

                <Grid item xs={12}>
                  <Divider sx={{ my: 1 }}>
                    <Typography variant="caption" color="textSecondary">
                      Indicaciones para el tecnico
                    </Typography>
                  </Divider>
                </Grid>
                <Grid item xs={12}>
                  <TextField fullWidth multiline rows={3} label="Descripcion / como hacerlo"
                    value={form.description || ''} onChange={set('description')}
                    helperText="Es lo primero que ve el tecnico al abrir la tarea" />
                </Grid>
                <Grid item xs={12}>
                  <TextField fullWidth multiline rows={2} label="Medidas de seguridad"
                    value={form.safety_notes || ''} onChange={set('safety_notes')}
                    helperText="Se le muestra destacado, en rojo, antes de empezar" />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <TextField fullWidth multiline rows={2} label="Herramientas necesarias"
                    value={form.required_tools || ''} onChange={set('required_tools')} />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <TextField fullWidth multiline rows={2} label="Materiales necesarios"
                    value={form.required_materials || ''} onChange={set('required_materials')} />
                </Grid>
                <Grid item xs={12}>
                  <TextField fullWidth multiline rows={2} label="Notas internas"
                    value={form.notes || ''} onChange={set('notes')} />
                </Grid>
                <Grid item xs={12}>
                  <FormControlLabel
                    control={<Checkbox checked={!!form.requires_photo}
                      onChange={(e) => setForm((f) => ({ ...f, requires_photo: e.target.checked }))} />}
                    label="Exigir foto para poder cerrarla" />
                </Grid>
              </Grid>
            )}

            {tab === 1 && (
              <Box>
                <Typography variant="body2" color="textSecondary" sx={{ mb: 1 }}>
                  Los puntos del checklist dan el avance de la tarea: el tecnico los va marcando
                  desde el movil.
                </Typography>
                <Box sx={{ display: 'flex', gap: 1, mb: 1 }}>
                  <TextField fullWidth size="small" label="Nuevo punto" value={newItem}
                    onChange={(e) => setNewItem(e.target.value)}
                    onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); addItem(); } }} />
                  <Button variant="outlined" startIcon={<AddIcon />} onClick={addItem}
                    disabled={!newItem.trim()}>
                    Anadir
                  </Button>
                </Box>
                {checklist.length === 0 ? (
                  <Typography variant="body2" color="textSecondary" sx={{ py: 3, textAlign: 'center' }}>
                    Sin puntos de checklist.
                  </Typography>
                ) : (
                  <List dense>
                    {checklist.map((item, idx) => (
                      <ListItem key={item.id} divider
                        secondaryAction={
                          <Tooltip title="Eliminar">
                            <IconButton edge="end" size="small" color="error"
                              onClick={() => removeItem(item, idx)}>
                              <DeleteIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                        }>
                        <ListItemIcon sx={{ minWidth: 40 }}>
                          <Checkbox edge="start" size="small" checked={!!item.is_done}
                            disabled={isNew} onChange={() => toggleItem(item)} />
                        </ListItemIcon>
                        <ListItemText primary={item.text}
                          primaryTypographyProps={{
                            sx: { textDecoration: item.is_done ? 'line-through' : 'none' },
                          }} />
                      </ListItem>
                    ))}
                  </List>
                )}
              </Box>
            )}

            {tab === 2 && !isNew && (
              <Box>
                <Box sx={{ display: 'flex', gap: 1, mb: 2 }}>
                  <TextField fullWidth size="small" multiline maxRows={4}
                    label="Comentario para el tecnico" value={comment}
                    onChange={(e) => setComment(e.target.value)} />
                  <Button variant="contained" startIcon={<SendIcon />} onClick={addComment}
                    disabled={!comment.trim() || busy}>
                    Enviar
                  </Button>
                </Box>
                {(task?.updates || []).length === 0 ? (
                  <Typography variant="body2" color="textSecondary" sx={{ py: 3, textAlign: 'center' }}>
                    Todavia no hay partes de trabajo ni comentarios.
                  </Typography>
                ) : (
                  <Stack spacing={1.5}>
                    {task.updates.map((u) => (
                      <Box key={u.id} sx={{ borderLeft: '3px solid', borderColor: 'divider', pl: 1.5 }}>
                        <Typography variant="caption" color="textSecondary">
                          {u.created_at?.slice(0, 16).replace('T', ' ')} · {u.author || 'Sistema'}
                        </Typography>
                        {(u.status_to || u.hours) && (
                          <Typography variant="body2" color="textSecondary">
                            {u.status_to ? STATUS_LABELS[u.status_to] || u.status_to : ''}
                            {u.hours ? ` · ${u.hours} h` : ''}
                          </Typography>
                        )}
                        {u.comment && <Typography variant="body2">{u.comment}</Typography>}
                        {u.photo_path && (
                          <Box sx={{ mt: 0.5, maxWidth: 180 }}>
                            <TaskPhoto filename={u.photo_path} driveLink={u.drive_web_link} />
                          </Box>
                        )}
                      </Box>
                    ))}
                  </Stack>
                )}
              </Box>
            )}
          </>
        )}
      </DialogContent>

      <DialogActions sx={{ px: 3, py: 2 }}>
        <Button onClick={onClose}>Cerrar</Button>
        {tab !== 2 && (
          <Button variant="contained" onClick={save} disabled={busy || loading || !form.title.trim()}>
            {busy ? 'Guardando...' : isNew ? 'Crear tarea' : 'Guardar'}
          </Button>
        )}
      </DialogActions>
    </Dialog>
  );
}
